import React, { useState } from "react";
import { motion } from "framer-motion";
import { CHARACTERS } from "../lib/content";
import Sparkles from "./Sparkles";
import CharacterLoreModal from "./CharacterLoreModal";
import { BookOpen } from "lucide-react";

const Characters = () => {
  const [active, setActive] = useState(CHARACTERS[0].id);
  const [loreOpen, setLoreOpen] = useState(false);
  const current = CHARACTERS.find((c) => c.id === active) || CHARACTERS[0];

  return (
    <section
      id="characters"
      data-testid="characters-section"
      className="relative py-28 md:py-40 overflow-hidden"
      style={{ background: "linear-gradient(180deg,#08140B 0%, #0D2214 50%, #08140B 100%)" }}
    >
      <Sparkles count={28} />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <span className="font-accent text-xl text-amber-300 tracking-wide">
            Meet the Wanderers
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mt-3">
            Friends of the <span className="italic text-gradient-gold">Undiscovered Land</span>
          </h2>
          <div className="section-divider mt-6" />
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Featured character display */}
          <motion.div
            key={current.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 relative"
          >
            <div className="relative max-w-md mx-auto" style={{ aspectRatio: "3/4.5" }}>
              <div className="absolute inset-0 rounded-[2rem] bg-[radial-gradient(circle_at_center,rgba(255,215,0,0.18),transparent_65%)] glow-pulse" />
              <div className="relative z-10 w-full h-full rounded-[2rem] overflow-hidden glass-card !p-0">
                <img
                  src={current.image}
                  alt={current.name}
                  className="w-full h-full object-cover object-top float-anim"
                  data-testid={`character-image-${current.id}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#08140B]/85 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <span className="font-accent text-amber-300 text-base">
                    {current.title}
                  </span>
                  <h3 className="font-heading text-4xl text-white leading-tight mt-1">
                    {current.name}
                  </h3>
                </div>
              </div>
            </div>

            <div className="mt-7 text-center lg:text-left lg:ml-2 max-w-md mx-auto lg:mx-0">
              <p className="text-emerald-50/80 leading-relaxed text-lg">
                {current.blurb}
              </p>
              <button
                onClick={() => setLoreOpen(true)}
                className="btn-gold mt-6 text-sm !py-2.5 !px-5"
                data-testid={`character-lore-btn-${current.id}`}
              >
                <BookOpen size={16} />
                Read the Tale of {current.name.split(" ").slice(-1)}
              </button>
            </div>
          </motion.div>

          {/* Character selector grid */}
          <div className="lg:col-span-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-4">
            {CHARACTERS.map((c) => (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                data-testid={`character-thumb-${c.id}`}
                className={`glass-card p-3 text-left transition-all duration-300 hover:-translate-y-1 ${
                  c.id === active
                    ? "ring-2 ring-amber-400/60"
                    : "opacity-80 hover:opacity-100"
                }`}
              >
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-emerald-950/50 mb-3">
                  <img
                    src={c.image}
                    alt={c.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="font-heading text-lg text-white leading-tight">
                  {c.name}
                </div>
                <div className="text-xs text-amber-300/80 font-accent mt-0.5">
                  {c.accent}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      <CharacterLoreModal
        character={current}
        open={loreOpen}
        onClose={() => setLoreOpen(false)}
      />
    </section>
  );
};

export default Characters;
